import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Typography
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import EmailIcon from '@mui/icons-material/Email';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { auth } from '../../../lib/google/firebase';
import PrimaryButton from '../../shared/button/PrimaryButton';
import GhostButton from '../../shared/button/GhostButton';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import { requestPasswordReset } from '../../../lib/auth/password';

interface PasswordResetModalProps {
  show_modal: boolean;
  onClose: () => void;
}

/**
 * A modal to handle the sending of password reset link from logged in state.
 * @param {boolean} show_modal State from parent to show modal when reset menu item is clicked.
 * @param {void} onClose Handles closing of modal when given condition is met.
 * @returns {JSX.Element}
 */
const PasswordResetModalApp = (props: PasswordResetModalProps): JSX.Element => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const handlePasswordReset = async (): Promise<void> => {
    if (!auth.currentUser?.email) {
      setFeedbackData({
        message: 'No email address found for current user.',
        state: true,
        type: 'error'
      });
      props.onClose();
      return;
    }

    setIsLoading(true);

    try {
      await requestPasswordReset(auth.currentUser.email);
      setFeedbackData({
        message: 'Password reset link sent to registered account email.',
        state: true,
        type: 'success'
      });
      props.onClose();
    } catch (err: unknown) {
      const trackingId: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PasswordResetModalApp failed on an unknown error while calling handlePasswordReset.',
          {},
          trackingId
        )
      );
      setFeedbackData({
        message: `Failed to send password reset email. Tracking ID: ${trackingId}`,
        state: true,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={props.show_modal}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <KeyIcon color="primary" />
          <Typography variant="h6" component="span">
            Reset Password
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Are you sure you want to reset your password? A password reset link will be sent to your
          registered email address.
        </DialogContentText>

        {auth.currentUser?.email && (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              bgcolor: 'grey.50',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}
          >
            <EmailIcon color="action" fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              Email will be sent to: <strong>{auth.currentUser.email}</strong>
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <GhostButton
          onClick={props.onClose}
          id="cancel-reset-button"
          outlined
          dark
          sx={{ minWidth: 120 }}
        >
          Cancel
        </GhostButton>
        <PrimaryButton
          onClick={handlePasswordReset}
          variant="contained"
          loading={isLoading}
          sx={{ minWidth: 120 }}
        >
          {isLoading ? 'Sending...' : 'Send Reset Link'}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordResetModalApp;
